import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";

// Composants
import { TabButton } from "./components/TabButton";
import { ProfessionPricingTab } from "./components/ProfessionPricingTab";
import { AddProfessionTab } from "./components/AddProfessionTab";
import { ImportClientsTab } from "./components/ImportClientsTab";
import { CityPricingTab } from "./components/CityPricingTab";
import PremiumDashboard from "./PremiumDashboard";
import { useProfessions } from "./hooks/useProfessions";
import { COLORS, API_URL } from "./types/profession.types";
import GlobalExportSettingsTab from './components/GlobalExportSettingsTab';

import "../../css/professionSettings.css";

const ProfessionSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("pricing");
  const { professions, refreshProfessions } = useProfessions();

  // Injection des styles CSS
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = inputStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleInitDefaultProfessions = async () => {
    if (!window.confirm("Créer les professions par défaut avec leurs prix ?")) return;

    try {
      const response = await axios.post(`${API_URL}/profession/init-default`);
      if (response.data.success) {
        toast.success("Professions initialisées avec succès !");
        refreshProfessions();
      } else {
        toast.error(response.data.message || "Erreur lors de l'initialisation");
      }
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de l'initialisation");
    }
  };

  return (
    <div className="profession-settings">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h1 className="h3 fw-normal" style={{ color: COLORS.text }}>
            Gestion des professions
          </h1>
          <p style={{ color: COLORS.textLight }}>
            Configurez les tarifs et options pour chaque profession
          </p>
        </div>
        
        <div className="d-flex flex-wrap gap-2">
          <Button
            variant="outline-primary"
            onClick={handleInitDefaultProfessions}
            style={{
              borderColor: COLORS.primary,
              color: COLORS.primary,
              whiteSpace: "nowrap"
            }}
          >
            Initialiser professions
          </Button>
          
          <div 
            className="px-3 py-2 rounded-pill"
            style={{ backgroundColor: COLORS.light, border: `1px solid ${COLORS.border}` }}
          >
            <span style={{ color: COLORS.text }}>
              <span style={{ color: COLORS.primary, fontWeight: 600 }}>{professions.length}</span> profession(s)
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 border-bottom" style={{ borderColor: COLORS.border }}>
        <div className="d-flex flex-wrap gap-2 gap-md-4">
          <TabButton
            active={activeTab === "pricing"}
            onClick={() => setActiveTab("pricing")}
            color={COLORS.primary}
          >
            Tarification
          </TabButton>
          
          <TabButton
            active={activeTab === "dashboard"}
            onClick={() => setActiveTab("dashboard")}
            color={COLORS.primary}
          >
            Dashboard
          </TabButton>
          
          <TabButton
            active={activeTab === "import"}
            onClick={() => setActiveTab("import")}
            color={COLORS.primary}
          >
            Importer des clients
          </TabButton>
          {/* <TabButton
            active={activeTab === "city-pricing"}
            onClick={() => setActiveTab("city-pricing")}
            color={COLORS.primary}
          >
            Tarifs par ville
          </TabButton> */}
          <TabButton
            active={activeTab === "global-export"}
            onClick={() => setActiveTab("global-export")}
            color={COLORS.primary}
          >
             Tarif Export Ville
          </TabButton>
          <TabButton
            active={activeTab === "add"}
            onClick={() => setActiveTab("add")}
            color={COLORS.primary}
          >
            + Ajouter
          </TabButton>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div>
        {activeTab === "pricing" && <ProfessionPricingTab />}
        {activeTab === "dashboard" && <PremiumDashboard />}
        {activeTab === "import" && <ImportClientsTab />}
        {activeTab === "global-export" && <GlobalExportSettingsTab />}
        {activeTab === "city-pricing" && <CityPricingTab />}
        {activeTab === "add" && (
          <AddProfessionTab onProfessionAdded={refreshProfessions} />
        )}
      </div>
    </div>
  );
};

// Styles CSS intégrés
const inputStyles = `
  @media (max-width: 768px) {
    input[type="text"], input[type="number"], input[type="email"],
    input[type="tel"], input[type="password"], select, textarea {
      font-size: 16px !important;
    }
    
    .form-control, .input-group { min-height: 44px; }
    .btn { min-height: 44px; min-width: 44px; }
    .table input.form-control-sm { height: 44px; padding: 0.5rem; }
  }

  input[type="number"] { -moz-appearance: textfield; }
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  
  .form-control:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 0.2rem rgba(75, 176, 192, 0.25);
  }
  
  .pricing-table-container {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .badge-custom {
    display: inline-block;
    padding: 0.35rem 0.65rem;
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: 20px;
  }
`;

export default ProfessionSettings;